import React from 'react';
import { X, Filter } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface FilterOption {
  id: string;
  label: string;
  description?: string;
}

const FREQUENCY_FILTERS: FilterOption[] = [
  { id: 'delta', label: 'Delta (0.5-4 Hz)', description: 'Deep sleep and relaxation' },
  { id: 'theta', label: 'Theta (4-8 Hz)', description: 'Meditation and creativity' },
  { id: 'alpha', label: 'Alpha (8-12 Hz)', description: 'Relaxation and stress reduction' },
  { id: 'beta', label: 'Beta (12-30 Hz)', description: 'Focus and concentration' },
  { id: 'gamma', label: 'Gamma (30-50 Hz)', description: 'Peak cognitive performance' }
];

const SOUND_TYPE_FILTERS: FilterOption[] = [
  { id: 'pure', label: 'Pure Frequencies', description: 'No ambient sounds' },
  { id: 'ambient', label: 'Ambient Sounds', description: 'With natural soundscapes' },
  { id: 'soundscape', label: 'Soundscapes', description: 'With atmospheric sounds' }
];

const DURATION_FILTERS: FilterOption[] = [
  { id: 'short', label: '5-15 minutes', description: 'Quick sessions' },
  { id: 'medium', label: '15-30 minutes', description: 'Standard sessions' },
  { id: 'long', label: '30+ minutes', description: 'Extended sessions' }
];

interface PresetFilterProps {
  onFilterChange: (filters: {
    frequencies: string[];
    soundTypes: string[];
    durations: string[];
  }) => void;
}

export function PresetFilter({ onFilterChange }: PresetFilterProps) {
  const [frequencies, setFrequencies] = React.useState<string[]>([]);
  const [soundTypes, setSoundTypes] = React.useState<string[]>([]);
  const [durations, setDurations] = React.useState<string[]>([]);

  const handleFrequencyToggle = (id: string) => {
    setFrequencies(prev => {
      const newFrequencies = prev.includes(id)
        ? prev.filter(f => f !== id)
        : [...prev, id];
      onFilterChange({ frequencies: newFrequencies, soundTypes, durations });
      return newFrequencies;
    });
  };

  const handleSoundTypeToggle = (id: string) => {
    setSoundTypes(prev => {
      const newSoundTypes = prev.includes(id)
        ? prev.filter(t => t !== id)
        : [...prev, id];
      onFilterChange({ frequencies, soundTypes: newSoundTypes, durations });
      return newSoundTypes;
    });
  };

  const handleDurationToggle = (id: string) => {
    setDurations(prev => {
      const newDurations = prev.includes(id)
        ? prev.filter(d => d !== id)
        : [...prev, id];
      onFilterChange({ frequencies, soundTypes, durations: newDurations });
      return newDurations;
    });
  };

  const clearFilters = () => {
    setFrequencies([]);
    setSoundTypes([]);
    setDurations([]);
    onFilterChange({ frequencies: [], soundTypes: [], durations: [] });
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 group text-muted-foreground hover:text-foreground">
          <Filter size={16} />
          <span className="w-0 overflow-hidden whitespace-nowrap transition-all duration-200 group-hover:w-auto group-hover:ml-2">
            Filter
          </span>
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[300px] sm:w-[400px]">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Filter Presets</SheetTitle>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear All
            </Button>
          </div>
        </SheetHeader>
        <ScrollArea className="h-full mt-4">
          <div className="space-y-6 pr-6">
            <div className="space-y-4">
              <h4 className="font-medium text-sm">Frequency Type</h4>
              {FREQUENCY_FILTERS.map((filter) => (
                <div key={filter.id} className="flex items-center justify-between space-x-2">
                  <div className="flex-1">
                    <Label htmlFor={filter.id} className="font-normal">
                      {filter.label}
                    </Label>
                    {filter.description && (
                      <p className="text-sm text-muted-foreground">{filter.description}</p>
                    )}
                  </div>
                  <Switch
                    id={filter.id}
                    checked={frequencies.includes(filter.id)}
                    onCheckedChange={() => handleFrequencyToggle(filter.id)}
                  />
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-4">
              <h4 className="font-medium text-sm">Sound Type</h4>
              {SOUND_TYPE_FILTERS.map((filter) => (
                <div key={filter.id} className="flex items-center justify-between space-x-2">
                  <div className="flex-1">
                    <Label htmlFor={filter.id} className="font-normal">
                      {filter.label}
                    </Label>
                    {filter.description && (
                      <p className="text-sm text-muted-foreground">{filter.description}</p>
                    )}
                  </div>
                  <Switch
                    id={filter.id}
                    checked={soundTypes.includes(filter.id)}
                    onCheckedChange={() => handleSoundTypeToggle(filter.id)}
                  />
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-4">
              <h4 className="font-medium text-sm">Duration</h4>
              {DURATION_FILTERS.map((filter) => (
                <div key={filter.id} className="flex items-center justify-between space-x-2">
                  <div className="flex-1">
                    <Label htmlFor={filter.id} className="font-normal">
                      {filter.label}
                    </Label>
                    {filter.description && (
                      <p className="text-sm text-muted-foreground">{filter.description}</p>
                    )}
                  </div>
                  <Switch
                    id={filter.id}
                    checked={durations.includes(filter.id)}
                    onCheckedChange={() => handleDurationToggle(filter.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
} 